import React, { useState, useEffect, Fragment } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useAdd from "../../../../api/useAdd";
import { useNavigate } from "react-router-dom";

const CollectDentalFees = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    treatment: null,
    amount: "",
    date: new Date().toISOString().split("T")[0],
  });
  const [formErrors, setFormErrors] = useState({});
  const [treatmentData, setTreatmentData] = useState([]);
  const [selectedTreatment, setSelectedTreatment] = useState(null);

  const { data: treatments, loading: loadingTreatments } = useFetchObjects(
    ["dental-treatments"],
    `dental-treatments/?status_not=4`,
    token,
  );

  const {
    handleAdd,
    resetForm,
    loading: saving,
  } = useAdd(
    "dental-payments",
    token,
    t("reception.paymentSuccess"),
    t("reception.paymentFailed"),
  );

  useEffect(() => {
    if (treatments) {
      setTreatmentData(
        treatments.map((treatment) => ({
          id: treatment.id,
          label: `${treatment.patient_name} - ${treatment.treatment_date}`,
          total_cost: treatment.total_cost,
          total_paid: treatment.total_paid,
          balance_due: treatment.balance_due,
        })),
      );
    }
  }, [treatments]);

  useEffect(() => {
    if (resetForm) {
      setFormData({
        treatment: null,
        amount: "",
        date: new Date().toISOString().split("T")[0],
      });
      setFormErrors({});
      setSelectedTreatment(null);
    }
  }, [resetForm]);

  const handleSave = (e) => {
    e.preventDefault();
    const errors = {};

    if (!formData.treatment)
      errors.treatment = t("reception.treatmentRequired");
    if (!formData.amount || formData.amount <= 0)
      errors.amount = t("reception.amountRequired");
    else if (
      selectedTreatment &&
      parseFloat(formData.amount) > parseFloat(selectedTreatment.balance_due)
    ) {
      errors.amount = t("reception.amountExceedsBalanceDue");
    }
    if (!formData.date) errors.date = t("reception.dateRequired");

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
    } else {
      handleAdd(formData);
    }
  };

  const handleTreatmentSelect = (value) => {
    setFormData({ ...formData, treatment: value?.id || null });
    setSelectedTreatment(value);
    setFormErrors({ ...formErrors, treatment: "" });
  };

  return (
    <Fragment>
      <Box
        sx={{
          background: "linear-gradient(to bottom, #f9fafa, #ffffff)",
          py: 4,
          minHeight: "60vh",
          width: "100%",
          p: 2,
        }}
      >
        {/* Go Back Icon and Title */}
        <Box display="flex" alignItems="center" mb={4}>
          <Tooltip title={t("reception.goBack")} arrow>
            <IconButton
              onClick={() => navigate(-1)}
              aria-label={t("reception.goBack")}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon size="large" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box mb={6} textAlign="center">
          <Typography
            variant="h3"
            fontWeight={700}
            color="primary"
            gutterBottom
            sx={{
              fontSize: isSmallScreen ? "1.8rem" : "2.5rem",
            }}
          >
            {t("reception.collectDentalFees")}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t("reception.collectFeesSubtitle")}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Payment Form Section */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: 3,
                boxShadow: 3,
                p: 3,
              }}
            >
              <Typography variant="h5" fontWeight={600} color="primary" mb={2}>
                {t("reception.paymentForm")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <form onSubmit={handleSave}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {loadingTreatments ? (
                      <CircularProgress />
                    ) : (
                      <Autocomplete
                        options={treatmentData}
                        getOptionLabel={(option) => option.label}
                        onChange={(e, value) => handleTreatmentSelect(value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("reception.selectTreatment")}
                            variant="outlined"
                            error={!!formErrors.treatment}
                            helperText={formErrors.treatment}
                            fullWidth
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("reception.amount")}
                      variant="outlined"
                      fullWidth
                      type="number"
                      value={formData.amount}
                      onChange={(e) =>
                        setFormData({ ...formData, amount: e.target.value })
                      }
                      error={!!formErrors.amount}
                      helperText={formErrors.amount}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("reception.date")}
                      variant="outlined"
                      fullWidth
                      type="date"
                      value={formData.date}
                      onChange={(e) =>
                        setFormData({ ...formData, date: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors.date}
                      helperText={formErrors.date}
                    />
                  </Grid>
                </Grid>

                <Box mt={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={saving}
                  >
                    {saving ? (
                      <CircularProgress size={20} />
                    ) : (
                      t("reception.save")
                    )}
                  </Button>
                </Box>
              </form>
            </Card>
          </Grid>

          {/* Treatment Details Section */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: 3,
                boxShadow: 3,
                overflow: "hidden",
              }}
            >
              <CardContent>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="primary"
                  mb={2}
                >
                  {t("reception.treatmentDetails")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {selectedTreatment ? (
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.patient")}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedTreatment.label}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.totalCost")}
                      </Typography>
                      <Typography variant="h6">
                        {selectedTreatment.total_cost}؋
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.totalPaid")}
                      </Typography>
                      <Typography variant="h6">
                        {selectedTreatment.total_paid}؋
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.balanceDue")}
                      </Typography>
                      <Typography variant="h6" color="error">
                        {selectedTreatment.balance_due}؋
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography textAlign="center" color="textSecondary">
                    {t("reception.noTreatmentSelected")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Fragment>
  );
};

export default CollectDentalFees;
